// Enhanced LoadingSpinner Component with improved accessibility
import React from 'react';
import { motion } from 'framer-motion';
import { FaSeedling } from 'react-icons/fa';
import LoadingGif from '../../../../static/loader.gif';

/**
 * Accessible loading spinner component
 * - Improved screen reader announcements
 * - Better semantic structure
 * - Non-visual loading indicators
 *
 * @returns {JSX.Element} Accessible loading spinner component
 */
const LoadingSpinner = () => {
  return (
    <motion.div
      className="uploader-loading-spinner text-center p-3"
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      role="status"
      aria-live="polite"
      aria-label="Processing your files, please wait">
      <div className="d-flex flex-column align-items-center gap-2">
        <motion.div
          animate={{
            scale: [1, 1.05, 1],
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          aria-hidden="true" // Hide animation from screen readers
        >
          <img
            src={LoadingGif}
            alt=""
            className="img-fluid"
            style={{
              width: '50px',
              height: '50px',
              objectFit: 'contain',
            }}
          />
        </motion.div>

        <motion.h3
          className="fw-bold m-0 h6 font-size-medium"
          animate={{ color: ['#2c3e50', '#27ae60', '#2c3e50'] }}
          transition={{ duration: 3, repeat: Infinity }}>
          Processing Your Files
        </motion.h3>

        <motion.div
          className="d-flex align-items-center text-muted small gap-2 font-size-small"
          animate={{ x: [0, 3, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          aria-hidden="true" // Hide animation from screen readers
        >
          <FaSeedling className="text-success icon-transparent size-small" size={14} />
          <span className="font-size-small">Please wait</span>
          <FaSeedling className="text-success icon-transparent size-small" size={14} />
        </motion.div>

        {/* Additional context for screen readers */}
        <div className="visually-hidden">
          Your files are currently being processed. This may take a few moments depending on the
          file size.
        </div>
      </div>
    </motion.div>
  );
};

export default LoadingSpinner;
